import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LegalModal from './LegalModal';
import DemoModal from './DemoModal';

interface FeatureDetail {
  icon: string;
  title: string;
  desc: string;
  longDesc: string;
  color: string;
  mockup: React.ReactNode;
}

const features: FeatureDetail[] = [
  {
    icon: "�",
    title: "Centru de Analiză Dosare",
    desc: "Încarcă documentele juridice, iar AI-ul nostru extrage automat părțile, termenele și legislația aplicabilă.",
    longDesc: "Transformăm fiecare PDF într-un dashboard inteligent de caz. Sistemul nostru OCR integrat cu Gemini identifică conflictele legale și propune strategii de acțiune în funcție de jurisprudență.",
    color: "from-blue-500 to-indigo-500",
    mockup: (
      <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-inner w-full max-w-[280px]">
        <div className="flex justify-between items-center mb-4">
          <span className="text-[10px] font-black uppercase text-indigo-600">Sumar AI: Cerere de Chemare...</span>
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
        </div>
        <div className="space-y-2">
          <div className="p-3 bg-indigo-50 rounded-xl border border-indigo-100 flex flex-col">
            <span className="text-[9px] font-black text-slate-500 uppercase">Termen Decădere</span>
            <span className="text-xs font-bold text-rose-600 italic">Întâmpinare: 15 zile de la comunicare</span>
          </div>
        </div>
      </div>
    )
  },
  {
    icon: "📅",
    title: "Ecosistem Sincronizat",
    desc: "Datele extrase din dosare alimentează automat calendarul tău și Editorul AI.",
    longDesc: "Termenele identificate se adaugă singure în agendă. Când redactezi un act nou, Editorul AI deja cunoaște contextul dosarului tău.",
    color: "from-indigo-500 to-purple-500",
    mockup: (
      <div className="bg-slate-900 rounded-2xl p-4 shadow-xl w-full max-w-[280px]">
        <div className="flex gap-2 mb-4">
          <div className="w-2 h-2 rounded-full bg-rose-500"></div>
          <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
        </div>
        <div className="space-y-3">
          <div className="bg-indigo-600 p-3 rounded-lg text-[10px] text-white font-bold leading-relaxed">
            Draft Generat: Întâmpinare<br/>
            <span className="text-[8px] font-normal opacity-70">Părți preluate din Cererea de fond.</span>
          </div>
        </div>
      </div>
    )
  },
  {
    icon: "�️",
    title: "Chat & Voice Contextual",
    desc: "Discută cu un asistent care îți cunoaște toate dosarele și termenele (RAG).",
    longDesc: "Nu mai trebuie să explici contextul. Întreabă 'Ce a zis martorul X în dosarul Y?' și primești răspuns cu citare exactă din documentele tale.",
    color: "from-emerald-500 to-teal-500",
    mockup: (
      <div className="bg-white border border-slate-100 rounded-2xl p-4 w-full max-w-[280px]">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600">�️</div>
          <span className="text-[10px] font-black text-slate-800 italic">Voice Assistant</span>
        </div>
        <p className="text-[10px] font-black text-emerald-600">"Da, conform contractului de la pagina 3..."</p>
      </div>
    )
  }
];

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [legalType, setLegalType] = useState<'terms' | 'gdpr' | 'cookies' | null>(null);
  const [showDemo, setShowDemo] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<FeatureDetail | null>(null);

  const scrollToBeta = () => {
    const element = document.getElementById('beta-access');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white overflow-x-hidden">
      <nav className="p-4 md:p-6 flex justify-between items-center bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-lg md:text-xl">L</span>
          </div>
          <span className="text-xl md:text-2xl font-bold text-slate-800 tracking-tight">LexSmart</span>
        </div>
        <div className="flex items-center gap-4 md:gap-8">
          <button
            onClick={scrollToBeta}
            className="text-slate-600 font-bold text-xs uppercase tracking-widest hover:text-indigo-600 transition-colors hidden sm:block"
          >
            Closed Beta
          </button>
          <button
            onClick={() => navigate('/login')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 md:px-8 py-2 md:py-2.5 rounded-full font-bold transition-all shadow-lg text-xs md:text-sm"
          >
            Login
          </button>
        </div>
      </nav>

      <header className="px-6 py-20 md:py-32 bg-[#0F172A] text-white text-center relative overflow-hidden min-h-[70vh] md:min-h-[85vh] flex items-center justify-center">
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] bg-indigo-600/20 rounded-full blur-[120px] animate-aurora-slow"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[60%] bg-blue-600/20 rounded-full blur-[120px] animate-aurora-fast"></div>
        </div>

        <div className="max-w-4xl mx-auto relative z-10 px-4">
          <div className="inline-block px-4 py-1.5 mb-6 md:mb-8 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-[10px] md:text-sm font-black tracking-[0.2em] uppercase">
            � Exclusiv pentru Avocați
          </div>
          <h1 className="text-4xl md:text-7xl lg:text-8xl font-black mb-6 md:mb-8 leading-[1.1] tracking-tight italic pb-4">
            <span className="inline-block px-2">Laboratorul Tău</span> <br />
            <span className="inline-block bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-blue-300 to-indigo-500 px-6 mx-[-1.5rem] py-2">
              Juridic cu AI
            </span>
          </h1>
          <p className="text-sm md:text-xl text-slate-400 mb-10 md:mb-14 max-w-2xl mx-auto leading-relaxed font-medium italic">
            Singura platformă din România care disecă documentele juridice, extrage termenele procedurale și coroborează datele cu jurisprudența. Acces pe bază de invitație.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 md:gap-6 px-4">
            <button 
              onClick={() => navigate('/register')}
              className="group relative bg-indigo-600 text-white px-8 md:px-10 py-4 md:py-6 rounded-2xl font-black text-sm md:text-lg hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-600/30 active:scale-95 overflow-hidden"
            >
              Solicită Invitație Beta
            </button>
            <button
               onClick={() => setShowDemo(true)}
               className="bg-white/5 text-white border border-white/10 px-8 md:px-10 py-4 md:py-6 rounded-2xl font-black text-sm md:text-lg hover:bg-white/10 transition-all backdrop-blur-sm active:scale-95"
            >
              Vezi Demo
            </button>
          </div>
        </div>
      </header>

      <section className="py-20 md:py-32 px-4 md:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 md:mb-24 px-4">
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-4 md:mb-6 italic tracking-tight">Analiză Chirurgicală</h2>
            <p className="text-slate-500 text-sm md:text-lg max-w-2xl mx-auto italic font-medium">Platforma noastră citește, înțelege și corelează datele din dosarele tale.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {features.map((f, i) => (
              <div 
                key={i} 
                onClick={() => setSelectedFeature(f)}
                className="p-8 md:p-10 bg-white rounded-[2rem] md:rounded-[3rem] border border-slate-100 shadow-xl hover:shadow-indigo-500/10 transition-all hover:-translate-y-2 cursor-pointer overflow-hidden group"
              >
                <div className="w-16 h-16 md:w-20 md:h-20 bg-slate-50 rounded-2xl flex items-center justify-center text-3xl md:text-4xl mb-6 md:mb-8 group-hover:bg-indigo-600 transition-colors group-hover:text-white">
                  {f.icon}
                </div>
                <h3 className="text-xl md:text-2xl font-black text-slate-900 mb-3 md:mb-4 tracking-tight italic group-hover:text-indigo-600 transition-colors">{f.title}</h3>
                <p className="text-slate-500 leading-relaxed font-medium italic text-xs md:text-sm">
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 px-4 md:px-6 bg-indigo-50" id="beta-access">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-20 h-20 bg-white rounded-[2rem] shadow-xl flex items-center justify-center text-4xl mx-auto mb-8 animate-bounce">
            💌
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-6 italic tracking-tight">Programul Closed Beta</h2>
          <p className="text-slate-600 italic font-medium max-w-2xl mx-auto mb-12 text-lg leading-relaxed">
            LexSmart este în prezent într-un stadiu de testare avansată (Closed Beta), destinat exclusiv avocaților legitimați. Oferim <strong className="text-indigo-600">acces gratuit</strong> și integral la toate funcțiile analitice, în schimbul feedback-ului dumneavoastră.
          </p>
          
          <div className="bg-white rounded-[3rem] p-10 md:p-16 shadow-2xl border border-indigo-100 max-w-3xl mx-auto text-left flex flex-col md:flex-row gap-12 items-center">
            <div className="flex-1 space-y-6">
              <h3 className="text-2xl font-black text-slate-900 italic tracking-tight">Cum funcționează?</h3>
              <ul className="space-y-4">
                <li className="flex gap-4 text-sm font-medium text-slate-600">
                  <span className="w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold shrink-0">1</span>
                  Solicitați o invitație completând formularul cu datele de barou.
                </li>
                <li className="flex gap-4 text-sm font-medium text-slate-600">
                  <span className="w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold shrink-0">2</span>
                  Echipa noastră va verifica apartenența profesională pentru a menține siguranța datelor.
                </li>
                <li className="flex gap-4 text-sm font-medium text-slate-600">
                  <span className="w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold shrink-0">3</span>
                  Primiți acces gratuit la Laboratorul AI pe toată durata programului Beta.
                </li>
              </ul>
            </div>
            <div className="w-full md:w-auto shrink-0">
               <button 
                 onClick={() => navigate('/register')}
                 className="w-full md:w-auto py-6 px-10 bg-slate-900 text-white rounded-3xl font-black text-xs uppercase tracking-widest hover:bg-indigo-600 transition-all shadow-xl active:scale-95"
               >
                 Vreau Invitație
               </button>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-12 md:py-20 border-t border-slate-100 bg-white">
        <div className="max-w-6xl mx-auto px-6 text-center md:text-left">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em]"> LexSmart AI Solutions - Exclusiv B2B</p>
            <div className="flex gap-6 md:gap-10 text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">
              <button onClick={() => setLegalType('terms')} className="hover:text-indigo-600 transition-colors">Termeni</button>
              <button onClick={() => setLegalType('gdpr')} className="hover:text-indigo-600 transition-colors">GDPR</button>
              <button onClick={() => setLegalType('cookies')} className="hover:text-indigo-600 transition-colors">Cookies</button>
            </div>
          </div>
        </div>
      </footer>

      {legalType && <LegalModal type={legalType} onClose={() => setLegalType(null)} />}
      {showDemo && <DemoModal onClose={() => setShowDemo(false)} onRegister={() => navigate('/register')} />}
    </div>
  );
};

export default LandingPage;
